import React from 'react'
import { t } from 'i18next'
import { FoodGroup } from '@/api/types'
import './IngredientsTableFilters.css'
import { Label } from '@/components/Label'
import { Select } from '@/components/Select'
import {
  IngredientGroupFilter,
  IngredientsSearchParams,
  UnitsFilter,
  VisibilityFilter,
} from '@/routes/ingredients.fcdb'
import { Switch } from '@/components/Switch'
import LanguaLSelector from '../LanguaLSelector'
import { Radio, RadioGroup } from '@/components/RadioGroup'

type Props = {
  filters: IngredientsSearchParams
  foodGroups: FoodGroup[]
  setSearchParams: <K extends keyof IngredientsSearchParams>(
    key: K,
    value: IngredientsSearchParams[K],
  ) => void
}

export default function IngredientsTableFilters({
  foodGroups,
  filters = {},
  setSearchParams,
}: Props) {
  // read filters from URL
  const {
    foodGroup: selectedFoodGroups,
    onlyUsedInRecipes,
    languaLID,
    includeLanguaLSubtree,
    unitsFilter,
    visibilityFilter,
    ingredientGroupFilter,
  } = filters

  return (
    <div className="IngredientTableQuickFilters">
      <h5>Filter by:</h5>
      <div className="FilterSection">
        <Label>{t('ingredient.foodGroups')}</Label>

        <Select
          isMulti
          closeMenuOnSelect={false}
          options={foodGroups}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.idGroup.toString()}
          value={
            (selectedFoodGroups
              ?.map((id) => foodGroups.find((fg) => fg.idGroup === id))
              .filter(Boolean) || []) as FoodGroup[]
          }
          onChange={(foodGroups) => {
            setSearchParams(
              'foodGroup',
              foodGroups.map((g) => g.idGroup),
            )
          }}
        />
      </div>
      <div className="FilterSection">
        <Switch
          isSelected={onlyUsedInRecipes}
          onChange={(value) => setSearchParams('onlyUsedInRecipes', value)}>
          {t('ingredient.onlyUsedInRecipes')}
        </Switch>
      </div>
      <div className="FilterSection">
        <Label>{t(`ingredient.unitsFilterLabel`)}</Label>
        <RadioGroup
          variation="segmented"
          value={unitsFilter}
          onChange={(value) => setSearchParams('unitsFilter', value as UnitsFilter)}>
          <Radio key="ALL" value="ALL">
            {t(`ingredient.unitsFilter.all`)}
          </Radio>
          <Radio key="WITH_UNITS" value="WITH_UNITS">
            {t(`ingredient.unitsFilter.with_units`)}
          </Radio>
          <Radio key="WITHOUT_UNITS" value="WITHOUT_UNITS">
            {t(`ingredient.unitsFilter.without_units`)}
          </Radio>
        </RadioGroup>
      </div>
      <div className="FilterSection">
        <Label>{t(`ingredient.visibilityFilterLabel`)}</Label>
        <RadioGroup
          variation="segmented"
          value={visibilityFilter}
          onChange={(value) => setSearchParams('visibilityFilter', value as VisibilityFilter)}>
          <Radio key="ALL" value="ALL">
            {t(`ingredient.visibilityFilter.all`)}
          </Radio>
          <Radio key="HIDDEN" value="HIDDEN">
            {t(`ingredient.visibilityFilter.hidden`)}
          </Radio>
          <Radio key="VISIBLE" value="VISIBLE">
            {t(`ingredient.visibilityFilter.visible`)}
          </Radio>
        </RadioGroup>
      </div>
      <div className="FilterSection">
        <Label>{t(`ingredient.ingredientGroupFilterLabel`)}</Label>
        <RadioGroup
          variation="segmented"
          value={ingredientGroupFilter}
          onChange={(value) =>
            setSearchParams('ingredientGroupFilter', value as IngredientGroupFilter)
          }>
          <Radio key="ALL" value="ALL">
            {t(`ingredient.ingredientGroupFilter.all`)}
          </Radio>
          <Radio key="WITH_IG" value="WITH_IG">
            {t(`ingredient.ingredientGroupFilter.with_ig`)}
          </Radio>
          <Radio key="WITHOUT_IG" value="WITHOUT_IG">
            {t(`ingredient.ingredientGroupFilter.without_ig`)}
          </Radio>
        </RadioGroup>
      </div>
      <div className="FilterSection">
        <Label>{t('ingredient.descriptors')}</Label>
        <LanguaLSelector
          enableSearch={true}
          includeSubtree={includeLanguaLSubtree}
          selectedDescriptorIDs={languaLID || []}
          onChangeSelection={(value) => {
            setSearchParams('languaLID', value)
          }}
          onChangeIncludeTree={(value) => setSearchParams('includeLanguaLSubtree', value)}
        />
      </div>
    </div>
  )
}
